/** The plain side panels next to the sign-in, sign-up and password forms. */
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { brand } from "@/brand";
import { priceLabel, type Plan } from "@/pages/account/platform";
import { usePlans, useRaOffer } from "@/site/data";
import { membershipPlan } from "@/site/membership";

import { AccountPanel, PanelList, PanelSteps } from "./AccountFrame";
import { trialPriceLabel, useTrialDays } from "./trial";

const LINK =
  "inline-flex items-center gap-1 font-semibold text-brand underline-offset-4 hover:underline";

/** What membership includes, in short (the full list is on the pricing page). */
const MEMBER_INCLUDES = [
  "Options and Stock Strategy Labs",
  "Market Atlas and Smart Money Tracker",
  "Screener, option chain and F&O dashboards",
  "Backtests and signal research",
  "Price, idea and strategy-score alerts",
  "Watchlists and live market pages",
];

/** Next to sign in: what membership costs, and where to start. */
export function LoginAside() {
  const plans = usePlans();
  const plan = membershipPlan(plans.data);
  const trialDays = useTrialDays();
  return (
    <>
      <AccountPanel
        title={
          trialDays > 0
            ? `New here? Start your ${trialDays}-day free trial`
            : "New here?"
        }
      >
        <p>
          Come research and invest with {brand.name}.{" "}
          {trialDays > 0
            ? `Full access to every tool for ${trialDays} days; no payment needed to start.`
            : ""}
          {plan ? ` After that, ${trialPriceLabel(plan, 0)}.` : ""}
        </p>
        <PanelList items={MEMBER_INCLUDES.slice(0, 4)} />
        <Link to="/signup" className={LINK}>
          {trialDays > 0 ? "Start your free trial" : "Create your account"}{" "}
          <ArrowRight className="size-4" aria-hidden />
        </Link>
        <p className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-3">
          <Link to="/pricing" className={LINK}>
            See pricing
          </Link>
          <Link to="/find-research-analyst" className={LINK}>
            Find a Research Analyst
          </Link>
        </p>
      </AccountPanel>
      <p className="px-1 text-xs text-subtle">
        Research tools and market data, not investment advice.
      </p>
    </>
  );
}

const STEPS = [
  "Create your account.",
  "Pay for your plan online by UPI, card or net banking.",
  "Your plan starts as soon as the payment goes through, and we email you a receipt.",
];

function trialSteps(days: number): string[] {
  return [
    "Create your account.",
    `Confirm your email: your ${days}-day free trial starts then. No payment needed now.`,
    `To continue after ${days} days, pay online by UPI, card or net banking; your membership continues at once.`,
  ];
}

function HowItWorks() {
  const days = useTrialDays();
  return (
    <AccountPanel title="How it works">
      <PanelSteps items={days > 0 ? trialSteps(days) : STEPS} />
    </AccountPanel>
  );
}

/** Next to the sign-up form: what membership includes, and how the trial works. */
export function InvestorAside({ plan }: { plan: Plan | undefined }) {
  const days = useTrialDays();
  return (
    <>
      <AccountPanel title="Every member gets">
        <PanelList items={MEMBER_INCLUDES} />
        {plan ? (
          <p className="text-xs">
            {days > 0 ? "After the trial" : "Membership"}: {priceLabel(plan)}.
          </p>
        ) : null}
      </AccountPanel>
      <HowItWorks />
    </>
  );
}

/** Next to the Research Analyst sign-up (V063): what an RA gets, and how verification works. */
export function RaAside() {
  const raTrial = useTrialDays("ra");
  const offer = useRaOffer();
  const offerNote = offer ? ` (${offer.label})` : "";
  return (
    <>
      <AccountPanel title="Every Research Analyst gets">
        <PanelList
          items={[
            "Every research tool on the platform, as members have",
            "Publish calls with the SEBI disclosures added for you; each call is kept unchanged",
            "A track record worked out from market prices, the same way for every call",
            "Invite your clients: their accounts are free, and your calls reach them in the app, by email and on WhatsApp",
            "A profile in our directory of SEBI-registered Research Analysts",
          ]}
        />
        <p className="text-xs">
          You collect your research fees from your clients directly; we never
          take a share.
        </p>
      </AccountPanel>
      <AccountPanel title="How verification works" className="bg-tint-green/60">
        <PanelSteps
          items={[
            "We look up your registration number in SEBI's register of Research Analysts.",
            "We email a 6-digit code to the address SEBI lists for that number.",
            raTrial > 0
              ? `Enter it and you're verified. Your ${raTrial}-day free trial starts when your email is confirmed; to continue after it, pay for your plan${offerNote}.`
              : `Enter it and you're verified. Then pay for your plan online${offerNote}; it starts as soon as the payment goes through.`,
          ]}
        />
      </AccountPanel>
    </>
  );
}

/** Next to the set / reset password form. */
export function PasswordAside({ reset }: { reset: boolean }) {
  return (
    <AccountPanel title="Need help?">
      <PanelList
        items={[
          reset
            ? "The reset link works once and expires in 2 hours. If it has expired, ask for a new one here."
            : "Use at least 10 characters. A password manager can make and remember one for you.",
          "No email? Check your spam folder.",
          brand.supportEmail ? (
            <>
              Still stuck? Write to{" "}
              <a
                href={`mailto:${brand.supportEmail}`}
                className="font-medium [overflow-wrap:anywhere] text-brand hover:underline"
              >
                {brand.supportEmail}
              </a>
              .
            </>
          ) : (
            "Still stuck? Use the contact page."
          ),
        ]}
      />
    </AccountPanel>
  );
}
